import { Plugin } from '@nuxt/types'
import { getTabName, injectScript } from '~/lib/utils/utils'

import {
  SAVED_SCHOLARSHIP_COUNT,
  IGNORED_SCHOLARSHIP_COUNT,
  STARTED_SCHOLARSHIP_COUNT,
  CAP_FSET,
  LEVEL_NAME,
  GOAL_SET,
  APPLICATION_SUBMITTED_COUNT,
  ELIGIBLE_SCHOLARSHIP_COUNT
} from '~/plugins/mixpanel/props'
import fillPropRule from '~/plugins/mixpanel/values'
import { EASY_APPLY_TAB, IGNORED_TAB } from '~/store/sideBar'
import { SAPPTrackingService } from '~/types/sapp-track'

const fillProps = (props, store) => props.reduce((acc, propName) => {
  const value = fillPropRule[propName](store)
  if (value === undefined) { return acc }
  acc[propName] = value
  return acc
}, {})

const updateUserPackageInformation = ({ store }) => {
  if (!store.getters['account/accountId'] || !window.localStorage) { return }

  let props = fillProps([APPLICATION_SUBMITTED_COUNT, ELIGIBLE_SCHOLARSHIP_COUNT], store)

  if (JSON.stringify(props) !== window.localStorage.getItem('user-package-props')) {
    try {
      props = JSON.stringify(props)
      localStorage.setItem('user-package-props', props)
    } catch (err) {
      store.$sentry.captureException(err)
      return
    }

    store.$mixpanel.updateUserInfo(props)
  }
}

const identifyAndUpdateInitUserInfo = ({ store }) => {
  // identify user
  if (store.getters['account/accountId']) {
    store.$mixpanel.identifyUser(store.getters['account/accountId'])
  }

  // update user info
  let props = [SAVED_SCHOLARSHIP_COUNT, IGNORED_SCHOLARSHIP_COUNT, STARTED_SCHOLARSHIP_COUNT, CAP_FSET]

  if (store.getters['featureSet/isGame']) {
    props = props.concat([LEVEL_NAME, GOAL_SET])
  }

  props = fillProps(props, store)

  store.$mixpanel.updateUserInfo(props)
}

const subscribers = {
  before: {
    '': []
  },
  after: {
    initStore: [
      updateUserPackageInformation,
      identifyAndUpdateInitUserInfo
    ],
    'cardList/changeItemProperty': [
      ({ action: { payload }, store }) => {
        const props = {
          isFavorite: [SAVED_SCHOLARSHIP_COUNT, fillPropRule[SAVED_SCHOLARSHIP_COUNT]],
          isIgnored: [IGNORED_SCHOLARSHIP_COUNT, fillPropRule[IGNORED_SCHOLARSHIP_COUNT]],
          requirementApplicationCount: [STARTED_SCHOLARSHIP_COUNT, fillPropRule[STARTED_SCHOLARSHIP_COUNT]]
        }

        if (!props[payload.propName]) { return }

        const [propName, propValue] = props[payload.propName]
        store.$mixpanel.updateUserInfo({
          [propName]: propValue(store)
        })
      },
      ({ action: { payload }, store }) => {
        const { id, propName, propValue } = payload
        if (propName !== 'isIgnored') { return }

        const tabName = getTabName(store.state.route)

        if (tabName === EASY_APPLY_TAB && propValue) {
          store.commit('tabList/setState', {
            stateName: 'initEasyScholarshipsLength',
            data: store.state.tabList.initEasyScholarshipsLength - 1
          })
        }

        if (tabName === IGNORED_TAB && !propValue) {
          const scholarship = store.getters['cardList/getListItem']({ storeName: 'scholarshipList', id })

          if (scholarship.isEasy) {
            store.commit('tabList/setState', {
              stateName: 'initEasyScholarshipsLength',
              data: store.state.tabList.initEasyScholarshipsLength + 1
            })
          }
        }
      }
    ],
    'requirements/setRequirement': [],
    'requirements/applyRequirement': [],
    'requirements/removeRequirement': [],
    'account/subscriptionCancel': [
      ({ $mixpanel }) => {
        $mixpanel.updateUserInfo({
          'Package ID': '',
          'Package name': '',
          Package: 'cancelled'
        })
      }
    ],
    'details/apply': [
      ({ action: { _payload }, state, store }) => {
        if (store.getters['featureSet/isGame']) {
          store.dispatch('gameStreak/getStreaks')
          store.dispatch('gameLevel/setLevel')

          store.$mixpanel.updateUserInfo({
            'Total points count': state.gameLevel.totalUserPoints
          })
        }
      },
      ({ action: { payload }, store, $sappTracking, $auth }) => {
        if (!payload.externalScholarshipId) { return }

        const sentScholarship = store.getters['cardList/getListItem'](
          { storeName: 'applicationList', id: payload.scholarshipId }
        )
        if (!sentScholarship) { return }

        ($sappTracking as SAPPTrackingService).trackApplied(payload.externalScholarshipId, $auth.user.id)
      }
    ],
    'account/setPaymentDetails': [
      ({ $sentry }) => {
        injectScript({
          src: 'https://js.stripe.com/v3/',
          id: 'stripe-sdk',
          error: () => $sentry.captureException(Error('An error occurred while loading Stripe SDK'))
        })
      }
    ]
  }
}

const processSubscribers = (order, context) => (action, state) => {
  const { type, payload } = action // Destructure `payload` here
  const _payload = payload // Assign `payload` to `_payload`

  if (!subscribers[order] || !subscribers[order][type]) {
    return
  }

  subscribers[order][type].forEach(handler => handler({ action: { type, payload: _payload }, state, ...context }))
}

const storeSubscribers: Plugin = (context) => {
  context.store.subscribeAction({
    before: processSubscribers('before', context),
    after: processSubscribers('after', context)
  })
}

export default storeSubscribers
